import {Component, Input, OnInit} from '@angular/core';
import {ContactDetailsResponse, ReferenceBookResponse, ReferenceBookType, SubjectApiService} from '@npck/af-frontend-api';
import {LocalizationService, NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {TableEvent} from '../../../../components/full-table/full-table.component';
import {Column, ColumnType, Row, SortEvent} from '../../../../components/full-table/table/table.component';
import {StatusStyle} from '../../../../components/status/status.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {formatMsisdnDisplay} from '../../../../domain/phone-utils';
import {CONTACT_DETAILS_ID, SUBJECT_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';

@Component({
    selector: 'af-subject-contacts-tab',
    standalone: false,
    templateUrl: './subject-contacts-tab.component.html',
})
export class SubjectContactsTabComponent implements OnInit {
    @Input() subjectId: string;
    dataSource: Row[] = [];
    sortedBy?: SortEvent;
    private rawData: Row[] = [];

    constructor(
        private subjectApiService: SubjectApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        this.loadContactDetails();
    }

    loadContactDetails(): void {
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TYPE),
            this.subjectApiService.getSubjectContactDetails(this.subjectId),
        ])
            .pipe(rxSpinner('contactDetails'))
            .subscribe(([contactTypes, response]) => {
                this.buildDataSource(response, contactTypes);
            });
    }

    onTableUpdate(event: TableEvent): void {
        this.sortedBy = event.sortBy;
        this.applySort();
    }

    private navigateToContact(contactDetailsId: string): void {
        this.routerService.navigate(Path.SUBJECTS_CONTACT_DETAILS_DETAILS, {
            pathParams: {[SUBJECT_ID]: this.subjectId, [CONTACT_DETAILS_ID]: contactDetailsId},
            queryParams: {[TAB]: 'tabPotentialVictim', [NESTED_TAB]: 'tabContacts'},
        });
    }

    private navigateToMsisdn(msisdnId: string): void {
        this.routerService.navigate(`${Path.SUBJECTS}/msisdn/${msisdnId}`);
    }

    private applySort(): void {
        if (!this.sortedBy) {
            this.dataSource = this.rawData;
            return;
        }
        const {columnName, direction} = this.sortedBy;
        const sign = direction === 'asc' ? 1 : -1;
        this.dataSource = [...this.rawData].sort((a, b) => {
            const aValue = (a[columnName] as Column)?.text ?? '';
            const bValue = (b[columnName] as Column)?.text ?? '';
            if (aValue === bValue) {
                return 0;
            }
            return aValue > bValue ? sign : -sign;
        });
    }

    private buildDataSource(
        response: ContactDetailsResponse[],
        contactTypes: ReferenceBookResponse[],
    ): void {
        this.rawData = response.map(item => {
            const isDeleted = !!item.isDeleted;
            return <Row>{
                click: () => this.navigateToContact(item.id),
                rowStyle: isDeleted ? 'disabled' : undefined,
                contactType: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(contactTypes, item.contactType, 'name_ru') ?? item.contactType,
                },
                contactDateTime: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.contactDate,
                },
                msisdn: <Column>{
                    type: item.msisdnId ? ColumnType.LINK : ColumnType.TEXT,
                    text: formatMsisdnDisplay(item.msisdn),
                    click: item.msisdnId ? () => this.navigateToMsisdn(item.msisdnId) : undefined,
                },
                status: <Column>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: isDeleted ? this.localizationService.getLocalizedKey('i18n_af_contactStatusDeleted') : null,
                    style: isDeleted ? StatusStyle.INACTIVE : null,
                },
                victimAppealDateTime: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.victimAppealDate,
                },
            };
        });
        this.applySort();
    }
}
